import api from './apiClient';
import {
  SubscriptionPlan,
  SubscriptionPlanCreate,
  UserSubscription,
  UserSubscriptionCreate,
  UserSubscriptionWithUsage,
  PlanChangePreview,
} from '../types';

export interface PublicPlan {
  id: number;
  name: string;
  description?: string | null;
  price: number;
  max_pets: number;
  features: string[];
}

export interface PublicPricing {
  trial_days: number;
  subscriptions_enabled: boolean;
  plans: PublicPlan[];
}

export const subscriptionService = {
  /** Planes y días de prueba para la landing; no requiere sesión. */
  getPublicPricing: async (): Promise<PublicPricing> => {
    const response = await api.get('/subscriptions/public-pricing');
    return response.data;
  },

  getPlans: async (activeOnly: boolean = true): Promise<SubscriptionPlan[]> => {
    const response = await api.get('/subscriptions/plans', {
      params: { active_only: activeOnly }
    });
    return response.data;
  },

  getPlan: async (planId: number): Promise<SubscriptionPlan> => {
    const response = await api.get(`/subscriptions/plans/${planId}`);
    return response.data;
  },

  getMySubscription: async (): Promise<UserSubscriptionWithUsage | null> => {
    const response = await api.get('/subscriptions/my-subscription');
    return response.data;
  },

  getAccessStatus: async (): Promise<{
    has_access: boolean;
    message: string;
    trial_end_date?: string | null;
    is_trial: boolean;
    days_remaining?: number | null;
    needs_subscription?: boolean;
  }> => {
    const response = await api.get('/subscriptions/access-status');
    return response.data;
  },

  getPlanChangePreview: async (planId: number): Promise<PlanChangePreview> => {
    const response = await api.get('/subscriptions/plan-change-preview', {
      params: { plan_id: planId },
    });
    return response.data;
  },

  applyFreePlanChange: async (planId: number): Promise<UserSubscription> => {
    const response = await api.post('/subscriptions/apply-plan-change', {
      plan_id: planId,
    });
    return response.data;
  },

  subscribe: async (subscriptionData: UserSubscriptionCreate): Promise<UserSubscription> => {
    const response = await api.post('/subscriptions/subscribe', subscriptionData);
    return response.data;
  },

  updateSubscription: async (subscriptionData: Partial<UserSubscriptionCreate>): Promise<UserSubscription> => {
    const response = await api.put('/subscriptions/my-subscription', subscriptionData);
    return response.data;
  },

  cancelSubscription: async (): Promise<{ message: string }> => {
    const response = await api.post('/subscriptions/my-subscription/cancel');
    return response.data;
  },

  createPlan: async (planData: SubscriptionPlanCreate): Promise<SubscriptionPlan> => {
    const response = await api.post('/subscriptions/plans', planData);
    return response.data;
  },

  updatePlan: async (planId: number, planData: Partial<SubscriptionPlanCreate>): Promise<SubscriptionPlan> => {
    const response = await api.put(`/subscriptions/plans/${planId}`, planData);
    return response.data;
  },

  deletePlan: async (planId: number): Promise<void> => {
    await api.delete(`/subscriptions/plans/${planId}`);
  },

  getMyPendingApprovals: async (): Promise<{
    total: number;
    pending_approvals: Array<{
      id: number;
      payment_provider: string;
      amount: number;
      currency: string;
      payment_date: string;
      created_at: string;
      status: string;
      user_notes?: string;
    }>;
  }> => {
    const response = await api.get('/subscriptions/my-pending-approvals');
    return response.data;
  },

  getMyPaymentHistory: async (params?: {
    status_filter?: string;
    limit?: number;
    offset?: number;
  }): Promise<{
    total: number;
    limit: number;
    offset: number;
      payment_history: Array<{
      id: number;
      source_type?: 'manual_approval' | 'free_plan_change';
      payment_provider: string;
      transaction_id: string;
      amount: number;
      currency: string;
      payment_date: string;
      created_at: string;
      status: string;
      user_notes?: string;
      admin_notes?: string;
      reviewed_at?: string;
      reviewed_by_name?: string;
      receipt_url?: string;
      receipt_file_name?: string;
      is_free_plan_change?: boolean;
    }>;
  }> => {
    const response = await api.get('/subscriptions/my-payment-history', { params });
    return response.data;
  },
};








